import React from 'react';
import { 
    Box, 
    Container, 
    Typography, 
    Grid, 
    Card, 
    CardContent, 
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import PublicIcon from '@mui/icons-material/Public';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningIcon from '@mui/icons-material/Warning';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

// Styled components
const StyledHero = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(8, 0),
    textAlign: 'center',
    color: 'white',
}));

const StyledSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6, 0),
}));

const PolicyCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    color: 'white',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

export default function ShippingPolicy() {
    const policyItems = [
        {
            icon: <AccessTimeIcon />,
            title: "Processing Time",
            content: [
                "All orders are processed within 1–2 business days after payment confirmation.",
                "Orders placed on weekends or public holidays will be processed on the next business day."
            ]
        },
        {
            icon: <LocalShippingIcon />,
            title: "Delivery Time",
            content: [
                "We aim to deliver all products within 3–7 business days across India.",
                "Delivery timelines may vary depending on the shipping address, courier partner availability, and unforeseen circumstances (such as strikes, weather conditions, or natural disasters)."
            ]
        },
        {
            icon: <MonetizationOnIcon />,
            title: "Shipping Charges",
            content: [
                "Standard shipping is free for all orders above ₹499.",
                "A nominal delivery charge of ₹50 applies to orders below this amount."
            ]
        },
        {
            icon: <TrackChangesIcon />,
            title: "Order Tracking",
            content: [
                "Once shipped, you will receive an email/SMS with the tracking ID and courier partner details.",
                "You can track your order in real time on our website or the courier's tracking portal."
            ]
        },
        {
            icon: <PublicIcon />,
            title: "Delivery Coverage",
            content: [
                "We currently ship across all serviceable pin codes in India.",
                "International shipping is not available at this time."
            ]
        },
        {
            icon: <PaymentIcon />,
            title: "Cash on Delivery (COD)",
            content: [
                "COD is available for select pin codes and orders under ₹2000.",
                "Our courier may contact you before delivery to confirm your availability."
            ]
        },
        {
            icon: <WarningIcon />,
            title: "Delays",
            content: [
                "While we strive to meet the 7-day delivery promise, delays may occur due to logistical issues.",
                "In case of delay beyond the committed timeline, our support team will proactively update you."
            ]
        },
        {
            icon: <ContactSupportIcon />,
            title: "Contact Us",
            content: [
                "For any queries related to your order or delivery, please contact us at info@glamourradiance.com or call us at +91-9868265827."
            ]
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <StyledHero>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h2" 
                        component="h1" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 'bold',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                        }}
                    >
                        Shipping Policy
                    </Typography>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            maxWidth: '800px', 
                            mx: 'auto',
                            lineHeight: 1.6,
                            fontWeight: 300,
                            opacity: 0.95
                        }}
                    >
                        Everything you need to know about our shipping and delivery process
                    </Typography>
                </Container>
            </StyledHero>

            {/* Policy Sections */}
            <StyledSection>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        {policyItems.map((item, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <PolicyCard>
                                    <CardContent sx={{ p: 4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                                            <IconWrapper>
                                                {item.icon}
                                            </IconWrapper>
                                            <Box sx={{ ml: 2, flex: 1 }}>
                                                <Typography 
                                                    variant="h5" 
                                                    component="h2" 
                                                    sx={{ 
                                                        color: 'primary.main',
                                                        fontWeight: 600,
                                                        mb: 2
                                                    }}
                                                >
                                                    {item.title}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <List sx={{ p: 0 }}>
                                            {item.content.map((point, pointIndex) => (
                                                <ListItem key={pointIndex} sx={{ px: 0, py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: '24px' }}>
                                                        <Box 
                                                            sx={{ 
                                                                width: '8px', 
                                                                height: '8px', 
                                                                borderRadius: '50%', 
                                                                backgroundColor: 'primary.main' 
                                                            }} 
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={point} 
                                                        sx={{ 
                                                            '& .MuiListItemText-primary': { 
                                                                fontSize: '1rem',
                                                                lineHeight: 1.6,
                                                                color: 'text.primary'
                                                            } 
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </PolicyCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </StyledSection>

            <Divider />

            {/* Additional Information Section */}
            <StyledSection sx={{ backgroundColor: 'grey.50' }}>
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                            variant="h4" 
                            component="h2" 
                            gutterBottom 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 3
                            }}
                        >
                            Important Notes
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Box 
                                    sx={{ 
                                        p: 3,
                                        borderRadius: '12px',
                                        backgroundColor: 'primary.50',
                                        border: '1px solid',
                                        borderColor: 'primary.100'
                                    }}
                                >
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            mb: 1
                                        }}
                                    >
                                        Fast Processing
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Orders processed within 1-2 business days
                                    </Typography>
                                </Box>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <Box 
                                    sx={{ 
                                        p: 3,
                                        borderRadius: '12px',
                                        backgroundColor: 'success.50',
                                        border: '1px solid',
                                        borderColor: 'success.100'
                                    }}
                                >
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            color: 'success.main',
                                            fontWeight: 600,
                                            mb: 1
                                        }}
                                    >
                                        Free Shipping
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        On orders above ₹499
                                    </Typography>
                                </Box>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <Box 
                                    sx={{ 
                                        p: 3,
                                        borderRadius: '12px',
                                        backgroundColor: 'warning.50',
                                        border: '1px solid',
                                        borderColor: 'warning.100'
                                    }}
                                >
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            color: 'warning.main',
                                            fontWeight: 600,
                                            mb: 1
                                        }}
                                    >
                                        Real-time Tracking
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Track your order every step of the way
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Container>
            </StyledSection>

            {/* Contact Section */}
            <StyledSection>
                <Container maxWidth="md">
                    <Box 
                        sx={{ 
                            textAlign: 'center',
                            p: 6,
                            backgroundColor: 'primary.50',
                            borderRadius: '24px',
                            border: '1px solid',
                            borderColor: 'primary.100'
                        }}
                    >
                        <ContactSupportIcon 
                            sx={{ 
                                fontSize: '4rem',
                                color: 'primary.main',
                                mb: 2
                            }} 
                        />
                        <Typography 
                            variant="h4" 
                            component="h3" 
                            gutterBottom 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 2
                            }}
                        >
                            Need Help?
                        </Typography>
                        <Typography 
                            variant="body1" 
                            color="text.secondary" 
                            sx={{ 
                                fontSize: '1.1rem',
                                lineHeight: 1.6,
                                mb: 3
                            }}
                        >
                            Our customer support team is always ready to assist you with any shipping-related queries.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Email: <Box component="span" color="primary.main">info@glamourradiance.com</Box>
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Phone: <Box component="span" color="primary.main">+91-9868265827</Box>
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </StyledSection>
        </Box>
    );
}